import { parseRuteroOfferExpiresAt } from '@bandido/shared/types';

export interface SelectedTravelQuote<TOffer> {
  readonly offer: TOffer;
  readonly quotedAt: number;
  readonly searchIdentity: string;
  /**
   * Vencimiento REAL de la oferta FIRMADO por el proveedor, en ms epoch, ya
   * validado contra el pattern cerrado del contrato al capturar
   * (`parseRuteroOfferExpiresAt` — hoy solo la oferta de transfers lo
   * publica). Ausente = el proveedor no publica vencimiento (o mandó un valor
   * fuera de contrato, que se descarta): rige solo la heurística local.
   *
   * Alimenta el DISPLAY del reloj (`fareCountdownAt`), que lo lee de acá y
   * nunca re-parsea la oferta. Es ADVISORY: el gate del hold deriva SOLO del
   * TTL local (skew-inmune) y la enforcement del firmado es del backend
   * (`OFFER_EXPIRED`) — ver el doc de `fareCountdownAt`.
   */
  readonly offerExpiresAt?: number;
}

/** The full quote authority shared by search keys and selected identities. */
export const quoteSearchAuthorityKey = (vertical: string, agencyId: string | number, payload: unknown) =>
  [vertical, agencyId, payload] as const;

function normalizeForIdentity(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeForIdentity);
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((normalized, key) => {
        const item = (value as Record<string, unknown>)[key];
        if (item !== undefined) normalized[key] = normalizeForIdentity(item);
        return normalized;
      }, {});
  }
  return value;
}

/**
 * Identidad normalizada de un valor cualquiera: el orden de las claves y las
 * ausencias no cambian la identidad. Es la ÚNICA normalización del buscador —
 * la comparten la key de cotización y la divergencia formulario↔resultados.
 */
export function searchPayloadIdentity(payload: unknown): string {
  return JSON.stringify(normalizeForIdentity(payload)) ?? '';
}

/** Stable identity for quote authority; UI-only filters stay outside it. */
export function quoteSearchIdentity(vertical: string, agencyId: string | number, payload: unknown): string {
  return searchPayloadIdentity(quoteSearchAuthorityKey(vertical, agencyId, payload));
}

export function captureSelectedQuote<TOffer>(
  offer: TOffer,
  quotedAt: number | undefined,
  searchIdentity: string,
): SelectedTravelQuote<TOffer> | null {
  if (!Number.isFinite(quotedAt) || (quotedAt ?? 0) <= 0 || !searchIdentity) {
    return null;
  }
  // El `expiresAt` firmado se lee y valida UNA vez, acá — el único punto por
  // el que pasa toda captura de oferta (search, re-quote, selección). Un valor
  // fuera del pattern del contrato se descarta y la cotización queda con la
  // heurística local, exactamente como una oferta sin el campo.
  const offerExpiresAt = parseRuteroOfferExpiresAt(
    (offer as { expiresAt?: unknown } | null | undefined)?.expiresAt,
  );
  return {
    offer,
    quotedAt: quotedAt as number,
    searchIdentity,
    ...(offerExpiresAt !== undefined ? { offerExpiresAt } : {}),
  };
}

/** Captures one result together with the exact committed generation it came from. */
export function captureSearchQuote<TOffer>(
  offer: TOffer,
  quotedAt: number | undefined,
  vertical: string,
  agencyId: string | number,
  committedPayload: unknown | null,
): SelectedTravelQuote<TOffer> | null {
  if (!vertical || agencyId === '' || committedPayload === null) return null;
  return captureSelectedQuote(offer, quotedAt, quoteSearchIdentity(vertical, agencyId, committedPayload));
}
